import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [resetEmail, setResetEmail] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      
      // Fetch user role after login
      const meResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${response.data.token}` }
      });
      
      const userRole = meResponse.data.role || "admin";
      
      // IMPORTANTE: Usa chiavi localStorage SEPARATE per ogni ruolo
      // Questo permette di avere Admin e Cleaning attivi nello stesso browser
      const tokenKey = userRole === "cleaning" ? "cleaning_token" : "admin_token";
      const userKey = userRole === "cleaning" ? "cleaning_user" : "admin_user";
      
      // Rimuovi il token dell'altro ruolo per evitare conflitti
      if (userRole === "cleaning") {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
      } else {
        localStorage.removeItem("cleaning_token");
        localStorage.removeItem("cleaning_user");
      }
      
      // Rimuovi anche i vecchi token generici (migrazione)
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      
      localStorage.setItem(tokenKey, response.data.token);
      localStorage.setItem(userKey, JSON.stringify({
        user_id: response.data.user_id,
        email: response.data.email,
        role: userRole
      }));
      
      // Redirect based on role
      if (userRole === "cleaning") {
        navigate("/admin/cleaning");
      } else {
        navigate("/admin/dashboard");
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Invalid credentials";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/password-reset-request`, { email: resetEmail });
      toast.success("If the email exists, a reset link has been sent.");
      setShowForgotPassword(false);
      setResetEmail("");
    } catch (error) {
      toast.error("Error sending reset email. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "#F9F8F6" }}>
      <div className="noise-overlay" />
      
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <a href="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_lucina-suite/artifacts/zplyxqc9_logo.png" 
              alt="Suite Lucina" 
              className="h-16 mx-auto mb-6"
            />
          </a>
          <h1 className="font-heading text-3xl text-[#1C1917]">
            {showForgotPassword ? "Reset Password" : "Admin Login"}
          </h1>
        </div>

        {showForgotPassword ? (
          /* Forgot Password Form */
          <form onSubmit={handleForgotPassword} className="space-y-6" data-testid="forgot-password-form">
            <div>
              <input
                type="email"
                placeholder="Email address"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                className="input-luxury"
                data-testid="reset-email"
              />
            </div>
            <button 
              type="submit" 
              className="btn-primary w-full"
              disabled={loading}
              data-testid="reset-submit"
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
            <button
              type="button"
              onClick={() => setShowForgotPassword(false)}
              className="block w-full text-center text-sm text-[#78716C] hover:text-[#1C1917] transition-colors"
            >
              Back to Login
            </button>
          </form>
        ) : (
          /* Login Form - NO REGISTRATION */
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <input
                type="email"
                placeholder="Email address"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="input-luxury"
                data-testid="login-email"
              />
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                minLength={6}
                className="input-luxury pr-12"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-0 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#1C1917]"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-[#78716C] hover:text-[#1C1917] transition-colors"
              data-testid="forgot-password-link"
            >
              Password dimenticata?
            </button>

            <button 
              type="submit" 
              className="btn-primary w-full"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? "Loading..." : "Login"}
            </button>
          </form>
        )}

        <div className="mt-12 text-center">
          <a href="/" className="text-sm text-[#78716C] hover:text-[#1C1917] transition-colors">
            ← Back to Website
          </a>
        </div>
      </div>
    </div>
  );
}
